/**
 * Home Group detail page — similar layout to EventPage.
 * Hero, "О группе", "Когда и где", "Ближайшая встреча".
 */

import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { PageLoader } from "../../components/PageLoader.tsx";
import { useHomeGroup } from "./hooks";
import { useEvents } from "@/features/events/hooks";
import {
  getDateKeyBangkok,
  getDayOfMonthAndWeekdayBangkok,
  formatDateShortBangkok,
  getTodayDateKeyBangkok,
} from "../../lib/bangkok-time.ts";
import {
  IconCalendar,
  IconHomeHeart,
  IconTelegram,
} from "../../components/Icons.tsx";
import { ErrorDisplay } from "../../components/ErrorDisplay.tsx";
import { EventCard } from "../../components/EventCard.tsx";
import type { Event } from "../../lib/graphql.ts";
import { getTelegram } from "../../lib/telegram.ts";

const WEEKDAYS_FULL = [
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
  "Воскресенье",
];

const CARD_CLASS =
  "rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50";

const DAY_OF_WEEK_LABELS: Record<string, string> = {
  MONDAY: "Пн",
  TUESDAY: "Вт",
  WEDNESDAY: "Ср",
  THURSDAY: "Чт",
  FRIDAY: "Пт",
  SATURDAY: "Сб",
  SUNDAY: "Вс",
};

function formatLeaderName(leader: {
  firstName: string | null;
  lastName: string | null;
}): string {
  const parts = [leader.firstName, leader.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "Лидер";
}

function formatSchedule(
  schedules: Array<{
    dayOfWeek: string;
    startTime: string;
    endTime: string | null;
    isActive: boolean;
  }>
): string {
  const active = schedules.filter((s) => s.isActive).slice(0, 5);
  return active
    .map((s) => {
      const part = `${DAY_OF_WEEK_LABELS[s.dayOfWeek] ?? s.dayOfWeek} ${
        s.startTime
      }`;
      return s.endTime ? `${part}–${s.endTime}` : part;
    })
    .join(", ");
}

function buildImageUrl(url: string | null | undefined): string | null {
  if (!url?.trim()) return null;
  if (url.startsWith("http")) return url;
  return url.startsWith("/") ? url : `/${url}`;
}

export function HomeGroupPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();

  // URQL hooks
  const {
    homeGroup: group,
    isLoading,
    error: groupError,
    refetch: refetchGroup,
  } = useHomeGroup(id);
  const {
    events,
    isLoading: isLoadingEvent,
    refetch: refetchEvents,
  } = useEvents({ homeGroupId: id, pause: !id });

  const error = groupError?.message ?? null;

  const handleRetry = () => {
    refetchGroup();
    refetchEvents();
  };

  const todayDateKey = getTodayDateKeyBangkok();
  const eventsByDay = useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const event of events) {
      const dateKey = getDateKeyBangkok(event.startsAt);
      const existing = map.get(dateKey);
      if (existing) {
        existing.push(event);
      } else {
        map.set(dateKey, [event]);
      }
    }
    for (const dayEvents of map.values()) {
      dayEvents.sort(
        (a, b) =>
          new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
      );
    }
    return map;
  }, [events]);

  const dayKeys = useMemo(
    () => Array.from(eventsByDay.keys()).sort(),
    [eventsByDay]
  );

  // Setup BackButton (Mini App / PWA: fallback при прямом заходе)
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/home-groups");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  if (isLoading) {
    return (
      <Layout>
        <PageLoader message="Загрузка..." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ErrorDisplay
          error={error}
          onRetry={handleRetry}
          className={CARD_CLASS}
        />
      </Layout>
    );
  }

  if (!group) {
    return (
      <Layout>
        <ErrorDisplay
          error={new Error("Группа не найдена")}
          onRetry={() => navigate("/home-groups")}
          title="Группа не найдена"
          buttonText="К списку групп"
          className={CARD_CLASS}
        />
      </Layout>
    );
  }

  const imageUrl = buildImageUrl(group.imageUrl);
  const scheduleStr = formatSchedule(group.schedules);
  const leaderName = formatLeaderName(group.leader);
  const coLeaderName = group.coLeader ? formatLeaderName(group.coLeader) : null;

  return (
    <Layout>
      {/* 1. Hero — image or placeholder (same size as EventPage) */}
      <div className="aspect-[4/3] overflow-hidden rounded-2xl mb-4 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 bg-slate-100">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={group.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-rose-100/90 to-slate-100/90 text-rose-400/80">
            <IconHomeHeart className="w-12 h-12" />
          </div>
        )}
      </div>

      {/* 2. Карточка «О группе» */}
      <div
        className={`${CARD_CLASS} divide-y divide-slate-100 mb-4 lg-mobile:mb-6`}
      >
        <div className="p-4 xs:p-5 md-mobile:p-6">
          <div className="flex items-start gap-2 mb-2">
            <h1 className="text-xl xs:text-2xl font-bold text-slate-900 flex-1 min-w-0">
              {group.name}
            </h1>
            {group.isOpen && (
              <span className="flex-shrink-0 px-2 py-1 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-lg">
                Открыта
              </span>
            )}
          </div>
          {(group.description || leaderName || coLeaderName) && (
            <div className="space-y-2">
              {group.description && (
                <p className="text-sm text-slate-600 whitespace-pre-line">
                  {group.description}
                </p>
              )}
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-500">
                <span>Лидер: {leaderName}</span>
                {coLeaderName && <span>Со-лидер: {coLeaderName}</span>}
              </div>
              {(group.leader.username || group.coLeader?.username) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {group.leader.username && (
                    <a
                      href={`https://t.me/${group.leader.username.replace(
                        /^@/,
                        ""
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 py-2 px-3 rounded-xl bg-sky-50/80 border border-sky-200/60 no-underline text-inherit active:scale-[0.98] transition-all [-webkit-tap-highlight-color:transparent]"
                    >
                      <IconTelegram className="w-4 h-4 text-sky-600/80 flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-700">
                        Написать лидеру
                      </span>
                    </a>
                  )}
                  {group.coLeader?.username && (
                    <a
                      href={`https://t.me/${group.coLeader.username.replace(
                        /^@/,
                        ""
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 py-2 px-3 rounded-xl bg-sky-50/80 border border-sky-200/60 no-underline text-inherit active:scale-[0.98] transition-all [-webkit-tap-highlight-color:transparent]"
                    >
                      <IconTelegram className="w-4 h-4 text-sky-600/80 flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-700">
                        Написать со-лидеру
                      </span>
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3. Секция «Когда» (расписание) — точка только в мероприятии */}
        {scheduleStr && (
          <div className="p-4 xs:p-5 md-mobile:p-6">
            <p className="text-sm text-slate-600 flex items-center gap-2">
              <IconCalendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
              {scheduleStr}
            </p>
          </div>
        )}
      </div>

      {/* 4. Ближайшие встречи (сгруппированы по дням, как в расписании) */}
      <section className="mt-6">
        <p className="section-title px-0">Ближайшие встречи</p>
        {isLoadingEvent ? (
          <div className={`${CARD_CLASS} p-4 mt-1`}>
            <div className="animate-pulse flex gap-3">
              <div className="w-16 h-16 rounded-xl bg-slate-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-200 rounded w-3/4" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
              </div>
            </div>
          </div>
        ) : dayKeys.length > 0 ? (
          <div className={`${CARD_CLASS} p-4 mt-1`}>
            <div className="space-y-6">
              {dayKeys.map((dateKey) => {
                const dayEvents = eventsByDay.get(dateKey) ?? [];
                const { weekdayIndex } =
                  getDayOfMonthAndWeekdayBangkok(dateKey);
                const dayName = WEEKDAYS_FULL[weekdayIndex];
                const dateStr = formatDateShortBangkok(dateKey);
                const isTodayDate = dateKey === todayDateKey;

                return (
                  <section key={dateKey}>
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">
                        {isTodayDate ? "Сегодня" : dayName}
                        <span className="font-normal text-slate-400 ml-2">
                          {dateStr}
                        </span>
                      </span>
                      <span className="text-xs text-slate-500">
                        {dayEvents.length}{" "}
                        {dayEvents.length === 1
                          ? "мероприятие"
                          : dayEvents.length < 5
                          ? "мероприятия"
                          : "мероприятий"}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {dayEvents.map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          linkState={{ from: `/home-group/${id}` }}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        ) : (
          <div className={`${CARD_CLASS} p-6 mt-1 text-center`}>
            <IconCalendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Ближайших встреч пока нет</p>
          </div>
        )}
      </section>
    </Layout>
  );
}
